import { Component, Input, OnInit } from '@angular/core';
import { DemandSignal, MarketFeedItem } from '../../models/sell.models';
import { MarketPulseService } from '../../../../services/market-pulse.service';
import {
  MarketPulseActivityItem,
  MarketPulseResponse,
} from '../../../../models/market-pulse.model';

const DEFAULT_FEED: MarketFeedItem[] = [
  { when: 'just now', text: 'someone searched <strong>SINGH</strong>' },
  { when: '2 min ago', text: 'dealer clicked through to <span class="plate sm">MX09 DAV</span>' },
  { when: '8 min', text: 'saved-search alert fired \u2014 6 buyers tracking <strong>name plates</strong>' },
  { when: '14 min', text: 'someone searched <strong>BIGDOG</strong>' },
  { when: '22 min', text: '<span class="plate sm">AB25 TOM</span> newly listed by seller' },
  { when: '31 min', text: 'dealer clicked through to <span class="plate sm">14 M</span>' },
  { when: '38 min', text: 'someone searched <strong>TOM</strong> \u2014 matched 4 plates' },
  { when: '47 min', text: 'saved-search alert fired \u2014 12 buyers tracking <strong>1-character</strong>' },
  { when: '52 min', text: '<span class="plate sm">A25 ETO</span> observed sold via DVLA' },
];

const DEFAULT_DEMAND: DemandSignal[] = [
  { num: '14', label: 'dealers in index holding dateless stock' },
  { num: '22', label: 'searches today in initials category' },
  { num: '6', label: 'recurring searches tracking TOM' },
];

const MAX_FEED_ITEMS = 15;

@Component({
  selector: 'pla-plate-x-live-market',
  templateUrl: './plate-x-live-market.component.html',
  styleUrls: ['./plate-x-live-market.component.scss'],
})
export class PlateXLiveMarketComponent implements OnInit {
  @Input() feedItems: MarketFeedItem[] = DEFAULT_FEED;
  @Input() demandSignals: DemandSignal[] = DEFAULT_DEMAND;

  @Input() searchTerms: string[] = ['SINGH', 'ADP', 'TOM', 'CRUISE', 'M', 'BIGDOG', 'DN', 'MAX', 'HASH', 'BASKET'];
  @Input() newlyListed: string[] = ['14 M', 'AB25 TOM', '4 EE', 'MOH BOY', 'M6 WAR'];
  @Input() recentlyMoved: string[] = ['HSS 23', 'A25 ETO', 'DR09 ZEE', 'KI4 YXO', 'C6 FMM'];

  constructor(private marketPulse: MarketPulseService) {}

  ngOnInit(): void {
    this.marketPulse.getMarketPulse().subscribe((res) => {
      const liveFeed = this.toFeedItems(res);
      if (liveFeed.length > 0) this.feedItems = liveFeed;

      const liveDemand = this.toDemandSignals(res);
      if (liveDemand) this.demandSignals = liveDemand;
    });
  }

  private toFeedItems(res: MarketPulseResponse | null): MarketFeedItem[] {
    if (!res || !res.activity || res.activity.length === 0) return [];
    return [...res.activity]
      .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime())
      .slice(0, MAX_FEED_ITEMS)
      .map((item) => ({
        when: relativeWhen(item.eventDate),
        text: describeActivityHtml(item),
      }));
  }

  private toDemandSignals(res: MarketPulseResponse | null): DemandSignal[] | null {
    if (!res || !res.demandSignals) return null;
    const d = res.demandSignals;
    const signals: DemandSignal[] = [];

    if (typeof d.dateletStockCount === 'number') {
      signals.push({ num: String(d.dateletStockCount), label: 'dealers in index holding dateless stock' });
    }
    if (typeof d.initialsSearchesToday === 'number') {
      signals.push({ num: String(d.initialsSearchesToday), label: 'searches today in initials category' });
    }
    if (d.tomTrackers && typeof d.tomTrackers.count === 'number' && d.tomTrackers.term) {
      signals.push({ num: String(d.tomTrackers.count), label: `recurring searches tracking ${d.tomTrackers.term}` });
    }
    return signals.length > 0 ? signals : null;
  }
}

function describeActivityHtml(item: MarketPulseActivityItem): string {
  const plate = escapeHtml(item.formattedRegistration || item.registration || '');
  const plateChip = `<span class="plate sm">${plate}</span>`;
  switch (item.type) {
    case 'sold':     return `${plateChip} observed sold via DVLA`;
    case 'listed':   return `${plateChip} newly listed by seller`;
    case 'viewed':   return `dealer clicked through to ${plateChip}`;
    case 'searched': return item.term
      ? `someone searched <strong>${escapeHtml(item.term)}</strong>`
      : `search activity on ${plateChip}`;
    default:         return plateChip;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':  return '&amp;';
      case '<':  return '&lt;';
      case '>':  return '&gt;';
      case '"':  return '&quot;';
      case "'":  return '&#39;';
      default:   return c;
    }
  });
}

function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recent';
  const diffMin = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (diffMin < 1)   return 'just now';
  if (diffMin < 60)  return `${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24)    return `${diffH}h`;
  const diffD = Math.round(diffH / 24);
  return `${diffD}d`;
}
